import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ListOrdered, PackageCheck, PackageX, Rocket, Truck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Button, cx, Tip } from "@/components/ui";
import { api, ApiError, type OrderReservation } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { intlLocale, t, tn } from "@/lib/i18n";

type Entry = NonNullable<OrderReservation["lines"]>[number];

/** Verfügbarkeit je Position – gleiche Abfrage wie „Platz n von m“ (geteilter Cache) */
export function useOrderAvailability(orderId: string, enabled: boolean) {
  const { tenantId } = useTenant();
  return useQuery({ queryKey: ["order-reservation", tenantId, orderId], queryFn: () => api.orders.reservation(tenantId, orderId), enabled, refetchInterval: 60_000 });
}

/** Einträge einer Position (Set: alle Bestandteile) */
export const entriesFor = (r: OrderReservation | undefined, productId: string | null) => (productId && r?.lines ? r.lines.filter((e) => e.forLines.includes(productId)) : []);

const missingOf = (e: Entry) => Math.max(0, e.needed - e.reserved);

/** Kopfzeile der Positionen: „1 von 2 Positionen lieferbar“ */
export function AvailabilitySummary({ r, lineProductIds }: { r: OrderReservation | undefined; lineProductIds: (string | null)[] }) {
  if (!r?.lines?.length) return null;
  const rows = lineProductIds.map((p) => entriesFor(r, p)).filter((es) => es.length);
  if (!rows.length) return null;
  const ok = rows.filter((es) => es.every((e) => missingOf(e) === 0)).length;
  const all = ok === rows.length;
  return (
    <span className={cx("ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal", all ? "bg-success-soft text-success" : "bg-warning-soft text-warning")}>
      {all ? <PackageCheck className="size-3.5" /> : <PackageX className="size-3.5" />}
      {all ? t("Alle Positionen reserviert") : t("{ok} von {n} Positionen lieferbar", { ok, n: rows.length })}
    </span>
  );
}

/** Bestellte Menge aufgeteilt: reserviert · im Lager, aber älteren Bestellungen zugeteilt · im Zulauf · fehlt ganz */
function parts(e: Entry) {
  const n = Math.max(0, e.needed);
  const reserved = Math.min(e.reserved, n);
  const held = Math.min(e.heldByOlder, e.inStock, n - reserved);
  const incoming = Math.min(e.incoming, n - reserved - held);
  return { n, reserved, held, incoming, gone: Math.max(0, n - reserved - held - incoming) };
}

/** Farbe des Fehlt-Hinweises: rot nur, wenn nichts zu holen ist (kein Bestand bei älteren, kein Zulauf) */
const obtainable = (e: Entry) => { const p = parts(e); return p.held + p.incoming > 0; };

/**
 * Füllstand der bestellten Menge: grün = für diesen Auftrag reserviert · gelb schraffiert = im Lager, aber älteren
 * Bestellungen zugeteilt (per „Vorziehen“ holbar) · blau = im Zulauf · leer = fehlt ganz. Erklärung als Tooltip.
 */
function Meter({ e }: { e: Entry }) {
  const p = parts(e);
  const w = (x: number) => `${p.n ? (x / p.n) * 100 : 0}%`;
  const legend = [
    t("{n} reserviert", { n: p.reserved }),
    p.held ? t("{n} im Lager, aber älteren Bestellungen zugeteilt", { n: p.held }) : null,
    p.incoming ? t("{n} im Zulauf", { n: p.incoming }) : null,
    p.gone ? t("{n} fehlen ganz", { n: p.gone }) : null,
  ].filter(Boolean).join(" · ");
  return (
    <Tip content={legend}>
      <span className="relative inline-flex h-1.5 w-14 shrink-0 cursor-help overflow-hidden rounded-full bg-surface-2" role="img" aria-label={legend}>
        <span className="h-full bg-success" style={{ width: w(p.reserved) }} />
        <span className="h-full" style={{ width: w(p.held), backgroundImage: "repeating-linear-gradient(135deg, var(--color-warning) 0 2px, transparent 2px 4px)" }} />
        <span className="h-full bg-info/70" style={{ width: w(p.incoming) }} />
      </span>
    </Tip>
  );
}

/**
 * Verfügbarkeit einer Position direkt in der Tabelle: reserviert, fehlt, wer den Bestand hält (ältere Bestellungen –
 * mit Link) und ob „Vorziehen“ die Position sofort lieferbar macht (bewusst gegen die Reihenfolge).
 */
export function LineAvailability({ orderId, entries, boostedAt }: { orderId: string; entries: Entry[]; boostedAt: string | null }) {
  if (!entries.length) return null;
  const set = entries.length > 1;
  const missing = entries.filter((e) => missingOf(e) > 0);
  if (!missing.length) {
    const e = entries[0]!;
    return (
      <div className="mt-1 flex items-center gap-2 text-[11.5px]">
        <Meter e={e} />
        <Tip content={t("Bestand ist für diesen Auftrag zurückgelegt – keine andere Bestellung bekommt ihn.")}>
          <span className="inline-flex items-center gap-1 font-medium text-success cursor-help"><PackageCheck className="size-3.5" />{set ? t("Set reserviert") : t("Reserviert")}</span>
        </Tip>
        {!set && <span className="text-fg-subtle">{t("{n} verfügbar", { n: fmtNumber(e.inStock) })}</span>}
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-1">
      {missing.map((e) => (
        <MissingRow key={e.productId} orderId={orderId} e={e} set={set} boostedAt={boostedAt} />
      ))}
    </div>
  );
}

function MissingRow({ orderId, e, set, boostedAt }: { orderId: string; e: Entry; set: boolean; boostedAt: string | null }) {
  const miss = missingOf(e);
  const none = e.inStock === 0;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px]">
      <Meter e={e} />
      <span className={cx("inline-flex items-center gap-1 font-semibold", obtainable(e) ? "text-warning" : "text-danger")}>
        <PackageX className="size-3.5" />
        {set ? t("{sku}: fehlt {n}", { sku: e.sku, n: miss }) : t("Fehlt {n}", { n: miss })}
      </span>
      {e.reserved > 0 && <span className="text-fg-muted">{t("{r} von {n} reserviert", { r: e.reserved, n: e.needed })}</span>}
      {none ? (
        (e.onHand ?? 0) > 0 ? (
          <Tip content={t("Physisch im Lager, aber nicht verfügbar: schon für Aufträge reserviert, die beim Lager sind, oder gesperrt.")}>
            <span className="text-fg-muted cursor-help">
              {(e.reservedInWarehouse ?? 0) > 0
                ? t("{n} im Lager, schon für übertragene Aufträge reserviert", { n: fmtNumber(e.onHand ?? 0) })
                : t("{n} im Lager, gesperrt", { n: fmtNumber(e.onHand ?? 0) })}
            </span>
          </Tip>
        ) : (
          <span className="text-fg-muted">{t("nicht im Lager")}</span>
        )
      ) : e.heldByOlder > 0 ? (
        <HoldersPopover orderId={orderId} e={e} boostedAt={boostedAt} />
      ) : null}
      {e.incoming > 0 && (
        <Tip content={t("Im Lager angekündigt, aber noch nicht eingebucht – sobald gebucht, wird automatisch neu verteilt.")}>
          <span className="inline-flex items-center gap-1 text-info cursor-help"><Truck className="size-3.5" />{t("{n} im Zulauf", { n: fmtNumber(e.incoming) })}</span>
        </Tip>
      )}
    </div>
  );
}

/** „3 im Lager · für 2 ältere Bestellungen reserviert ⌄“ – Liste mit Links und „Vorziehen“ */
function HoldersPopover({ orderId, e, boostedAt }: { orderId: string; e: Entry; boostedAt: string | null }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const boost = useMutation({
    mutationFn: () => api.orders.boostReservation(tenantId, orderId, true),
    onSuccess: () => {
      toast.success(t("Vorgezogen"), { description: t("Der Auftrag bekommt jetzt vor den älteren Bestellungen Bestand und geht gleich ans Lager.") });
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["order-reservation", tenantId, orderId] });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, orderId] });
    },
    onError: (err) => toast.error(t("Nicht möglich"), err instanceof ApiError ? { description: (err.body as { message?: string })?.message } : undefined),
  });
  const more = e.holderCount - e.holders.length;
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button type="button" className={cx("inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1 -mx-1 text-fg-muted hover:bg-surface-2 hover:text-fg", open && "bg-surface-2 text-fg")}>
          <ListOrdered className="size-3.5" />
          {t("{s} verfügbar", { s: fmtNumber(e.inStock) })} · {tn(e.holderCount, "1 ältere Bestellung zuerst", "{n} ältere Bestellungen zuerst")}
          <ChevronDown className={cx("size-3 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content side="bottom" align="start" sideOffset={6} collisionPadding={12} className="z-50 w-[340px] rounded-2xl border border-border bg-surface p-3 shadow-xl animate-fade-in">
          <p className="text-[12.5px] font-semibold">{e.sku}</p>
          <p className="mt-0.5 text-[12px] text-fg-muted">
            {t("{s} Stk. verfügbar – sie stehen Bestellungen zu, die vor diesem Auftrag eingegangen sind (wer zuerst bestellt, bekommt zuerst).", { s: fmtNumber(e.inStock) })}
          </p>
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
            {e.holders.map((h) => (
              <li key={h.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px]">
                <Link to={`/orders?open=${h.id}`} onClick={() => setOpen(false)} className="font-medium tabular-nums underline underline-offset-2">{h.orderNumber}</Link>
                <span className="text-fg-subtle">{h.placedAt ? new Date(h.placedAt).toLocaleString(intlLocale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                <span className="ml-auto tabular-nums text-fg-muted">{t("{n} Stk.", { n: h.quantity })}</span>
              </li>
            ))}
            {more > 0 && <li className="px-3 py-1.5 text-[12px] text-fg-subtle">{t("und {n} weitere", { n: more })}</li>}
          </ul>
          {e.boostWouldCover && !boostedAt ? (
            <div className="mt-3 rounded-xl bg-warning-soft p-2.5">
              <p className="text-[12px]">{t("Mit „Vorziehen“ bekommt dieser Auftrag den Bestand sofort – die älteren Bestellungen warten dann auf den nächsten Wareneingang.")}</p>
              <Button size="sm" className="mt-2" icon={<Rocket className="size-3.5" />} loading={boost.isPending} onClick={() => boost.mutate()}>{t("Vorziehen – jetzt beliefern")}</Button>
            </div>
          ) : boostedAt ? (
            <p className="mt-2 text-[12px] text-fg-muted">{t("Bereits vorgezogen – andere vorgezogene Bestellungen kamen früher dran.")}</p>
          ) : (
            <p className="mt-2 text-[12px] text-fg-muted">{t("Auch vorgezogen reicht der Bestand im Lager nicht für die ganze Menge.")}</p>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
